import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Career = () => {
  const navigate = useNavigate();

  const handleApplyClick = () => {
    window.scrollTo(0, 0);
    navigate('/Job-application');
  };

  const jobs = [
    {
      title: "Business Development Executive",
      location: "On Site - Chennai",
      duration: "Full-time",
      stipend: "Up to ₹10,000 per month stipend",
      description: "Join our dynamic business development team and gain valuable experience in client engagement, business growth, and relationship building. This role offers excellent exposure to the business side of technology and opportunities to develop strong professional networks.",
      responsibilities: [
        "Engage with potential clients and understand their business needs",
        "Support the business development team in identifying new opportunities",
        "Assist in preparing presentations and proposals for clients",
        "Build and maintain relationships with key stakeholders",
        "Participate in networking events and business meetings"
      ],
      qualifications: [
        "Any discipline with strong communication skills",
        "Excellent verbal and written communication abilities",
        "Strong negotiation and presentation skills",
        "Enthusiastic about business development and client relations",
        "Ability to work in a fast-paced environment"
      ]
    },
    {
      title: "Digital Marketing Executive",
      location: "On Site - Chennai",
      duration: "Full-time",
      stipend: "Up to ₹10,000 per month stipend",
      description: "Dive into the exciting world of digital marketing and gain hands-on experience in social media marketing, content creation, and SEO/SEM strategies. You'll work with our marketing team to create engaging campaigns and help grow our online presence.",
      responsibilities: [
        "Create engaging content for social media platforms",
        "Assist in developing and implementing digital marketing campaigns",
        "Support SEO/SEM initiatives to improve online visibility",
        "Analyze social media metrics and prepare performance reports",
        "Collaborate with design team on visual content creation"
      ],
      qualifications: [
        "Marketing, Mass Communication, BBA, or related fields preferred",
        "Creative thinking and innovative mindset",
        "Social media savvy with understanding of various platforms",
        "Basic design skills and familiarity with design tools",
        "Strong written communication skills"
      ]
    }
  ];

  const benefits = [
    {
      title: "Learning Opportunities",
      description: "Gain practical experience working with the latest technologies in the industry",
      icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    },
    {
      title: "Mentorship",
      description: "Get valuable guidance from industry experts who will help you grow and succeed",
      icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    },
    {
      title: "Work Environment",
      description: "Enjoy a supportive, collaborative, and flexible work culture",
      icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Careers – Join Our Team
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            At Vista Tech, we are always looking for passionate, driven individuals to join our growing team. 
            If you're eager to kickstart your career in tech, our internship program offers a fantastic opportunity 
            for hands-on experience in a supportive environment.
          </p>
        </motion.div>

        {/* Job Listings */}
        {jobs.map((job, jobIndex) => (
          <motion.div
            key={job.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 + (jobIndex * 0.2) }}
            className="bg-white rounded-xl shadow-lg p-8 mb-16"
          >
            <h2 className="text-2xl font-bold text-primary mb-4">{job.title}</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="flex items-center space-x-2 text-gray-600">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{job.location}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{job.duration}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{job.stipend}</span>
              </div>
            </div>

            <p className="text-gray-600 mb-8">
              {job.description}
            </p>

            {/* Responsibilities */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-primary mb-4">Responsibilities:</h3>
              <ul className="space-y-2">
                {job.responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-secondary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Qualifications */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-primary mb-4">Qualifications:</h3>
              <ul className="space-y-2">
                {job.qualifications.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-secondary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Apply Button */}
            <div className="mt-8 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleApplyClick}
                className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
              >
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Apply Now</span>
              </motion.button>
            </div>
          </motion.div>
        ))}

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center text-primary">Why Join Vista Tech?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-primary">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Career;